import { useSuspenseQuery } from '@apollo/client'
import { VERSION } from '@salescore/buff-common'
import { FetchMeDocument } from '@salescore/client-api'
import { openIntercom, openMiitel, Posthog, Tracker } from '@salescore/client-base'
import {
  ApolloErrorComponentContext,
  getOrganizationIdFromPath,
  HandleQuery,
  setCurrentOrganizationIdToLocalStorage,
  SuspenseWithLoading,
} from '@salescore/client-common'
import { ReduxProvider } from '@salescore/client-redux'
import { BUILTIN_USER_ROLES } from '@salescore/features'
import { logger, LoggerEvents, organizationHasFeature } from '@salescore/frontend-common'
import { useLocation, useMatches } from '@tanstack/react-router'
import { App, Layout } from 'antd'
import dayjs from 'dayjs'
import { isNotNullish } from 'effect/Predicate'
import platform from 'platform'
import { type ReactElement, type ReactNode, useEffect } from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { useTitle } from 'react-use'
import { RecoilRoot } from 'recoil'

import { GlobalLauncher } from '../../presentation/components/launcher/GlobalLauncher'
import { AuthenticatedApolloProvider } from '../../providers/AuthenticatedApolloProvider'
import { ErrorProvider } from '../../providers/ErrorProvider.tsx'
import { I18nProvider } from '../../providers/I18nProvider'
import { useHandleApolloError } from '../hooks/useHandleApolloError.tsx'
import { SiderV2 } from '../member/SiderV2'
import { AgentWidgetContainer } from './AgentWidgetContainer'

const prefix = process.env.APP_ENV === 'prod' ? undefined : `[${process.env.APP_ENV}]`

const DefaultLayoutContent = ({
  children,
  organizationId,
}: {
  children: ReactElement
  organizationId: string
}): ReactNode => {
  const location = useLocation()
  const matches = useMatches()

  const {
    data: { myUser },
  } = useSuspenseQuery(FetchMeDocument, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  })
  useTitle([prefix, myUser.organization.name, 'SALESCORE'].filter(isNotNullish).join(' | '))

  const { identity: myIdentity, organization } = myUser
  const me = {
    myUser,
    myIdentity,
    organization,
  }
  useEffect(() => {
    setCurrentOrganizationIdToLocalStorage(me.myUser.organization.id)
  }, [me.myUser.organization.id])

  const posthogProperty = {
    name: `${myUser.name} / ${myUser.organization.name}`,
    email: myUser.identity.email,
    role: myUser.role,
    plan: myUser.plan,
    organizationId: myUser.organization.id,
    organizationName: myUser.organization.name,
    userId: myUser.id,
    userName: myUser.name,
    signUpDate: myUser.createdAt,
    // posthog.ts側で不要なidentityやpeople.setが走ってトラッキング回数が増えるのを防ぐため、キャッシュの機構をいれているが、
    // posthogの挙動が怪しく、property全てが記録されないような挙動を見せている。
    // 何度かやり直すとこれが防げるようなので、とりあえず日毎に1回やり直すため、以下のpropertyを設定しておく
    propertyConfiguredAt: dayjs().format(`YYYY-MM-DD`),
    platform: platform.toString(),
  }
  const posthogProperties = [
    { name: posthogProperty.name },
    { email: posthogProperty.email },
    { role: posthogProperty.role },
    { plan: posthogProperty.plan },
    // { description: posthogproperty.meta.label },
    { organizationId: posthogProperty.organizationId },
    { organizationName: posthogProperty.organizationName },
    { userId: posthogProperty.userId },
    { userName: posthogProperty.userName },
    { signUpDate: posthogProperty.signUpDate },
    { propertyConfiguredAt: posthogProperty.propertyConfiguredAt },
    { platform: posthogProperty.platform },
  ]

  useEffect(() => {
    if (myUser.role !== BUILTIN_USER_ROLES.admin) {
      Posthog.identifyOnce(myUser.id, posthogProperties)
      Posthog.setPeopleOnce(myUser.id, posthogProperties) // identifyOnceでもpropertyの設定はできるが、一度設定した後に上書きができないので、プロパティが変わった際はここで上書きする
      Posthog.trackAccess(location.pathname, posthogProperty)
    }
  }, [location.pathname, myUser.id, myUser.role, posthogProperties, posthogProperty])

  useEffect(() => {
    // eslint-disable-next-line no-console -- このまま出力したいのでconsoleを使っている
    console.info(`SALESCOREへようこそ！お使いのバージョンは${VERSION}です`)
    logger.info(LoggerEvents.VISIT, `/ ${posthogProperty.name}`, posthogProperty)
  }, [])

  useEffect(() => {
    Tracker.shared().setUser(
      { id: myUser.organization.id, name: myUser.organization.name },
      { id: myUser.id, name: myUser.name },
    )

    openMiitel({
      companyId: organization.setting.miitelCompanyId,
      accessKey: organization.setting.miitelAccessKey,
    })
    openIntercom({
      name: `${myUser.name} / ${myUser.organization.name}`,
      email: myUser.identity.email,
      user: myUser,
    })
  }, [myUser.id])

  // 以下、effect内で行わないと、setUserの前にtrackが走ったりするので注意
  useEffect(() => {
    Tracker.shared().track({ category: 'visit', value: location.pathname })
  }, [location.pathname])

  // fullPathだと'/'の有無で挙動がおかしいのでrouteIdにした
  const isViewRelated = matches.at(-1)?.routeId === '/o/$organizationId/v2/'
  // エージェントは、組織の機能フラグ enable_ai_agent が ON のときだけ描き、使えるかの判定はウィジェットが読む status に任せる。
  // 機能フラグの行がない組織（デフォルト）では描かず、機能を使わない大多数の組織に status の要求を出さない
  const isAgentEnabled = organizationHasFeature(organization, 'enable_ai_agent')

  return (
    <ErrorProvider myUser={myUser} myIdentity={myIdentity}>
      <App component={false}>
        <ApolloErrorComponentContext.Provider value={useHandleApolloError}>
          <HandleQuery>
            <SuspenseWithLoading>
              <Layout style={{ height: '100%' }}>
                {isViewRelated ? <></> : <SiderV2 organizationId={organizationId} />}
                {/* eslint-disable-next-line better-tailwindcss/no-unknown-classes -- overflow-visible-print は main.less にスタイル定義あり */}
                <Layout.Content className={`overflow-visible-print h-full overflow-y-auto`}>
                  <SwitchTransition>
                    <CSSTransition key={location.pathname} timeout={200} classNames="page-transition">
                      <SuspenseWithLoading>
                        <ApolloErrorComponentContext.Provider value={useHandleApolloError}>
                          {children}
                        </ApolloErrorComponentContext.Provider>
                      </SuspenseWithLoading>
                    </CSSTransition>
                  </SwitchTransition>
                </Layout.Content>
                <GlobalLauncher />
                {isAgentEnabled && (
                  <AgentWidgetContainer
                    organizationId={organizationId}
                    isViewRelated={isViewRelated}
                    defaultOpenViewIds={organization.setting.defaultOpenViewIds}
                  />
                )}
              </Layout>
            </SuspenseWithLoading>
          </HandleQuery>
        </ApolloErrorComponentContext.Provider>
      </App>
    </ErrorProvider>
  )
}

export const MemberLayoutBase = ({ children }: { children: ReactElement }): ReactElement => {
  const organizationId = getOrganizationIdFromPath()

  return (
    <AuthenticatedApolloProvider>
      <I18nProvider>
        <RecoilRoot>
          <ReduxProvider>
            <DefaultLayoutContent organizationId={organizationId}>{children}</DefaultLayoutContent>
          </ReduxProvider>
        </RecoilRoot>
      </I18nProvider>
    </AuthenticatedApolloProvider>
  )
}
